import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Action, ConfirmModal, Modal, NumberInput } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '~/api';
import {
  useClaim,
  useIsLocked,
} from '~/features/claimReview/ClaimReviewProvider';
import { useClaimLedgerSummary } from '~/features/claimReview/hooks/useClaimLedgerSummary';
import { useCurrency } from '~/hooks/useCurrency';
import { useStoreId } from '~/providers/StoreUserProvider';
import { FeeLineItem } from '~/components/lineItems/FeeLineItem';

type Fee = NonNullable<CrewMerchantUi.ClaimLineItem['fees']>[number];

const claimLineItemFeeUpdate = CrewMerchantUi.claimLineItemFeeUpdateSchema;

function EditFeeModal({
  claimLineItemId,
  fee,
  onClose,
}: {
  claimLineItemId: CrewMerchantUi.ClaimLineItem['id'];
  fee: Fee;
  onClose: () => void;
}) {
  const claim = useClaim();
  const storeId = useStoreId();
  const claimLedgerSummary = useClaimLedgerSummary();
  const { currencyCode, currencySymbol } = useCurrency();

  const { mutateAsync: updateFee, isPending: isUpdating } = useMutation({
    mutationFn: api
      .store(storeId)
      .claim(`${claim.id}`)
      .lineItem(`${claimLineItemId}`)
      .fee(`${fee.id}`).update,
    onSettled: async () =>
      Promise.all([claim.refetch(), claimLedgerSummary.refetch()]),
  });

  const formId = `edit-line-item-fee-${fee.id}`;
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm({
    resolver: zodResolver(claimLineItemFeeUpdate),
    defaultValues: {
      amount: fee.amount,
    } satisfies CrewMerchantUi.ClaimLineItemFeeUpdate,
  });

  const onSubmit: FormEventHandler = (event) => {
    handleSubmit(async ({ amount }) => {
      await updateFee({ amount });
      onClose();
    })(event).catch(console.error);
  };

  return (
    <Modal
      title="Edit Fee"
      show
      onClose={onClose}
      actions={
        <>
          <Action onClick={onClose}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isUpdating}
            loading={isUpdating}
          >
            Save
          </Action>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        <NumberInput
          id={`line-item-fee-amount-${fee.id}`}
          label={fee.displayName}
          min={0}
          step={0.01}
          details="The amount that was collected, or will be deducted at finalization."
          addon={{
            insideStart: currencySymbol,
            insideEnd: currencyCode,
          }}
          required
          {...register('amount', { valueAsNumber: true })}
          error={errors.amount?.message}
        />
      </form>
    </Modal>
  );
}

function FeeRow({
  claimLineItemId,
  fee,
}: {
  claimLineItemId: CrewMerchantUi.ClaimLineItem['id'];
  fee: Fee;
}) {
  const claim = useClaim();
  const isLocked = useIsLocked();

  const storeId = useStoreId();
  const claimLedgerSummary = useClaimLedgerSummary();

  const [showEdit, setShowEdit] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  const { mutateAsync: removeFee, isPending: isRemoving } = useMutation({
    mutationFn: api
      .store(storeId)
      .claim(`${claim.id}`)
      .lineItem(`${claimLineItemId}`)
      .fee(`${fee.id}`).remove,
    onSettled: async () =>
      Promise.all([claim.refetch(), claimLedgerSummary.refetch()]),
  });

  return (
    <li className="flex items-center gap-2">
      <div className="grow">
        <FeeLineItem
          fee={{
            ...fee,
            name: fee.displayName,
            kind: 'handling',
          }}
        />
      </div>
      {!isLocked && (
        <div className="flex items-start gap-1">
          <Action
            accessibilityLabel="Edit fee"
            icon="edit"
            variant="ghost"
            onClick={() => setShowEdit(true)}
          />
          <Action
            accessibilityLabel="Remove fee"
            icon="remove"
            variant="ghost"
            onClick={() => setShowConfirmRemove(true)}
          />
        </div>
      )}

      {showEdit && (
        <EditFeeModal
          claimLineItemId={claimLineItemId}
          fee={fee}
          onClose={() => setShowEdit(false)}
        />
      )}
      <ConfirmModal
        title="Remove Fee"
        prompt={`Are you sure you want to remove the ${fee.displayName} fee?`}
        confirmText="Remove"
        show={showConfirmRemove}
        loading={isRemoving}
        onCancel={() => setShowConfirmRemove(false)}
        onConfirm={() => {
          removeFee()
            .then(() => setShowConfirmRemove(false))
            .catch(console.error);
        }}
      />
    </li>
  );
}

export default function ClaimLineItemFees({
  claimLineItemId,
  fees,
}: {
  claimLineItemId: CrewMerchantUi.ClaimLineItem['id'];
  fees: CrewMerchantUi.ClaimLineItem['fees'];
}) {
  if (!fees?.length) return null;

  return (
    <section className="space-y-2">
      <p className="text-xs">Fees</p>
      <ul className="space-y-2 py-2">
        {fees.map((fee) => (
          <FeeRow key={fee.id} claimLineItemId={claimLineItemId} fee={fee} />
        ))}
      </ul>
    </section>
  );
}
